import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useSiteConfig } from '../context/SiteConfigContext';
import { Navbar, Container, makeTheme } from './LandingPage';
import MasonryGallery from '../components/common/MasonryGallery';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

function useMediaQuery(q) {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const fn = () => setM(mq.matches);
    fn();
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, [q]);
  return m;
}

const SECTIONS = [
  { key: 'live', label: 'Diffusions en direct', icon: '🔴' },
  { key: 'video', label: 'Vidéos', icon: '🎬' },
  { key: 'image', label: 'Photos', icon: '📷' },
];

function fmtDate(d) {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }); }
  catch { return d; }
}

function useColumns() {
  const [columns, setColumns] = useState(3);
  useEffect(() => {
    const compute = () => {
      const w = window.innerWidth;
      if (w < 640) setColumns(1);
      else if (w < 1024) setColumns(2);
      else if (w < 1280) setColumns(3);
      else setColumns(4);
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, []);
  return columns;
}

function MediaTestimonialCard({ item, onSelect }) {
  const isLive = item.media_type === 'live';
  return (
    <div
      onClick={() => onSelect(item)}
      style={{ position: 'relative', borderRadius: 16, overflow: 'hidden', cursor: 'pointer', background: isLive ? '#0f172a' : '#e5e5e5' }}
    >
      {isLive ? (
        item.file_url ? (
          <img src={item.file_url} alt={item.caption || 'Diffusion en direct'} style={{ width: '100%', display: 'block', aspectRatio: '16/9', objectFit: 'cover' }} />
        ) : (
          <div style={{ width: '100%', aspectRatio: '16/9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <span style={{ width: 16, height: 16, borderRadius: '50%', background: '#ef4444', display: 'inline-block' }} />
          </div>
        )
      ) : item.media_type === 'video' ? (
        <video src={item.file_url} style={{ width: '100%', display: 'block' }} muted playsInline />
      ) : (
        <img
          src={item.file_url}
          alt={item.caption || item.activity_name}
          style={{ width: '100%', display: 'block' }}
          onError={(e) => { e.target.onerror = null; e.target.src = 'https://placehold.co/800x600/e5e5e5/525252?text=Photo'; }}
        />
      )}
      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom, rgba(0,0,0,.55) 0%, rgba(0,0,0,.1) 35%, transparent 60%)' }} />
      {isLive && (
        <span style={{ position: 'absolute', top: 14, left: 14, background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 800, borderRadius: 6, padding: '3px 9px', letterSpacing: '0.03em' }}>
          EN DIRECT
        </span>
      )}
      {item.church_name && (
        <div style={{ position: 'absolute', top: 14, right: 14, display: 'flex', alignItems: 'center', gap: 6, color: '#fff' }}>
          <span style={{ width: 22, height: 22, borderRadius: '50%', background: 'rgba(255,255,255,.25)', border: '1.5px solid rgba(255,255,255,.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 800 }}>
            {item.church_name[0]}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, textShadow: '0 1px 3px rgba(0,0,0,.5)' }}>{item.church_name}</span>
        </div>
      )}
      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 14px 14px' }}>
        <p style={{ fontSize: 13.5, fontWeight: 700, color: '#fff', margin: 0, textShadow: '0 1px 3px rgba(0,0,0,.5)', lineHeight: 1.3 }}>
          {item.caption || item.activity_name || (isLive ? 'Diffusion en direct' : '')}
        </p>
      </div>
    </div>
  );
}

export default function PublicGallery() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { config } = useSiteConfig();
  const mobile = useMediaQuery('(max-width: 860px)');
  const columns = useColumns();
  const [gallery, setGallery] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const theme = makeTheme(config.primaryColor || '#000000');

  useEffect(() => {
    axios.get(`${API}/activities/gallery/?limit=200`)
      .then(r => setGallery(Array.isArray(r.data) ? r.data : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    axios.get(`${API}/activities/public/`)
      .then(r => {
        const list = Array.isArray(r.data) ? r.data : [];
        setUpcoming(list.filter(a => a.status === 'planned' || a.status === 'ongoing'));
      })
      .catch(() => {});
  }, []);

  const handleSelect = (m) => {
    setSelectedMedia(m);
  };

  return (
    <div style={{ fontFamily: "system-ui,-apple-system,'Segoe UI',Roboto,sans-serif", minHeight: '100vh', background: theme.app }}>
      <Navbar
        config={{ brand: config.siteName || 'RCC', logoUrl: config.logoUrl || '' }}
        theme={theme} mobile={mobile}
        onLogin={() => navigate('/login')} onFidele={() => navigate('/fidele')}
        user={user} navigate={navigate} hasEvents={false} hasGallery={gallery.length > 0}
      />

      <Container style={{ paddingTop: mobile ? 110 : 140, paddingBottom: 90 }}>
        <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, color: theme.body, fontWeight: 600, fontSize: 14, marginBottom: 28, padding: 0 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><polyline points="15 18 9 12 15 6" /></svg>
          Retour à l'accueil
        </button>

        <div style={{ textAlign: mobile ? 'left' : 'center', marginBottom: mobile ? 40 : 60 }}>
          <h1 style={{ fontSize: mobile ? 30 : 48, fontWeight: 800, letterSpacing: '-0.03em', color: theme.ink, margin: '0 0 12px' }}>
            Notre Galerie
          </h1>
          <p style={{ fontSize: mobile ? 15 : 17, color: theme.body, margin: '0 auto', maxWidth: 560 }}>
            Photos, vidéos et diffusions en direct des moments partagés en famille.
          </p>
        </div>

        {/* ── Prochainement ── */}
        {upcoming.length > 0 && (
          <div style={{ marginBottom: 60 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
              <span style={{ fontSize: 20 }}>📅</span>
              <h2 style={{ fontSize: 20, fontWeight: 800, color: theme.ink, margin: 0 }}>Prochainement</h2>
              <span style={{ fontSize: 13, color: theme.muted, fontWeight: 600 }}>({upcoming.length})</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 14 }}>
              {upcoming.slice(0, 6).map(a => (
                <div key={a.id} style={{ background: '#fff', border: `1px solid ${theme.line}`, borderRadius: 14, padding: '16px 18px', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {a.status === 'ongoing' && <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#22c55e', display: 'inline-block' }} />}
                    <span style={{ fontSize: 11.5, fontWeight: 700, color: theme.primaryStrong, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      {a.status === 'ongoing' ? 'En cours' : 'À venir'}
                    </span>
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: theme.ink }}>{a.name}</div>
                  <div style={{ fontSize: 13, color: theme.muted, textTransform: 'capitalize' }}>
                    {fmtDate(a.date)}{a.start_time ? ` · ${a.start_time}` : ''}
                  </div>
                  {a.location && <div style={{ fontSize: 12.5, color: theme.muted }}>{a.location}</div>}
                </div>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <p style={{ color: theme.muted, textAlign: 'center' }}>Chargement...</p>
        ) : gallery.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: theme.muted }}>
            Aucun média disponible pour le moment.
          </div>
        ) : (
          SECTIONS.map(section => {
            const items = gallery.filter(m => m.media_type === section.key);
            if (items.length === 0) return null;
            return (
              <div key={section.key} style={{ marginBottom: 60 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 24 }}>
                  <span style={{ fontSize: 20 }}>{section.icon}</span>
                  <h2 style={{ fontSize: 22, fontWeight: 800, color: theme.ink, margin: 0 }}>{section.label}</h2>
                  <span style={{ fontSize: 13, color: theme.muted, fontWeight: 600 }}>({items.length})</span>
                </div>
                <MasonryGallery
                  items={items}
                  columns={columns}
                  gap={16}
                  renderItem={(item) => <MediaTestimonialCard item={item} onSelect={handleSelect} />}
                />
              </div>
            );
          })
        )}
      </Container>

      {selectedMedia && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.85)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: mobile ? 12 : 24 }}
          onClick={() => setSelectedMedia(null)}>
          <div onClick={e => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 18, width: '100%', maxWidth: 960, maxHeight: '88vh', overflow: 'hidden', display: 'flex', flexDirection: mobile ? 'column' : 'row', boxShadow: '0 30px 80px rgba(0,0,0,.4)' }}>
            {/* Média à gauche */}
            <div style={{ flex: mobile ? 'none' : '1.1', background: '#0f172a', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: mobile ? 220 : 'auto', maxHeight: mobile ? 260 : '88vh' }}>
              {selectedMedia.media_type === 'video' ? (
                <video src={selectedMedia.file_url} controls autoPlay style={{ width: '100%', height: '100%', maxHeight: mobile ? 260 : '88vh', objectFit: 'contain' }} />
              ) : (
                <img src={selectedMedia.file_url} alt={selectedMedia.caption || selectedMedia.activity_name} style={{ width: '100%', height: '100%', maxHeight: mobile ? 260 : '88vh', objectFit: 'contain' }} />
              )}
            </div>
            {/* Description à droite */}
            <div style={{ flex: 1, padding: mobile ? 20 : 32, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
              <button onClick={() => setSelectedMedia(null)} style={{ alignSelf: 'flex-end', background: theme.app, border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: theme.body, fontSize: 15, marginBottom: 12 }}>✕</button>
              <h2 style={{ fontSize: mobile ? 19 : 23, fontWeight: 800, color: theme.ink, margin: '0 0 10px' }}>
                {selectedMedia.caption || selectedMedia.activity_name || 'Sans titre'}
              </h2>
              {selectedMedia.activity_name && selectedMedia.activity_name !== selectedMedia.caption && (
                <div style={{ fontSize: 13.5, fontWeight: 700, color: theme.primaryStrong, marginBottom: 4 }}>
                  {selectedMedia.activity_name}
                </div>
              )}
              {selectedMedia.activity_date && (
                <div style={{ fontSize: 13, color: theme.muted, marginBottom: 8 }}>
                  {fmtDate(selectedMedia.activity_date)}
                </div>
              )}
              {selectedMedia.church_name && (
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 600, color: theme.muted, background: theme.app, borderRadius: 999, padding: '4px 11px', width: 'fit-content', marginBottom: 16 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"><path d="M3 9l9-7 9 7v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z" /></svg>
                  {selectedMedia.church_name}
                </div>
              )}
              {selectedMedia.description && (
                <p style={{ fontSize: 14.5, lineHeight: 1.7, color: theme.body, margin: '0 0 20px', whiteSpace: 'pre-line' }}>
                  {selectedMedia.description}
                </p>
              )}
              {(selectedMedia.links?.length > 0 || selectedMedia.external_url) && (
                <div style={{ marginTop: 'auto', display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  {selectedMedia.links?.length > 0 ? selectedMedia.links.map((l, i) => (
                    <a key={i} href={l.url} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: theme.primary, color: '#fff', borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: 'none', width: 'fit-content' }}>
                      {l.label || 'Voir le lien'}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                    </a>
                  )) : (
                    <a href={selectedMedia.external_url} target="_blank" rel="noreferrer"
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '11px 18px', background: theme.primary, color: '#fff', borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: 'none', width: 'fit-content' }}>
                      Voir le lien
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /><polyline points="15 3 21 3 21 9" /><line x1="10" y1="14" x2="21" y2="3" /></svg>
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
